import React, { useEffect, useState } from "react";
import { PageHeader, Content } from "@/components/PageChrome";
import { api, formatApiError } from "@/lib/api";
import { Settings2, Save, Users } from "lucide-react";
import { toast } from "sonner";

const inputCls =
  "w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm " +
  "focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 outline-none";

/**
 * Platform-wide company-limit policy.
 *
 * `default_company_limit` is what new accounts start with; individual accounts
 * can be moved above or below it from the admin panel, so this page only sets
 * the baseline rather than editing every user.
 */
export default function AdminSettings() {
  const [defaults, setDefaults] = useState({ default_company_limit: 3, max_company_limit: 25 });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draftLimit, setDraftLimit] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [settings, list] = await Promise.all([
        api.get("/admin/settings"),
        api.get("/admin/users"),
      ]);
      setDefaults({
        default_company_limit: settings.data?.default_company_limit ?? 3,
        max_company_limit: settings.data?.max_company_limit ?? 25,
      });
      setUsers(Array.isArray(list.data) ? list.data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveDefaults = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", defaults);
      toast.success("Platform defaults updated");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveUserLimit = async (u) => {
    setBusyId(u.id);
    try {
      await api.put(`/admin/users/${u.id}/company-limit`, {
        company_limit: Number(draftLimit),
      });
      toast.success(`Company limit updated for ${u.email}`);
      setEditingId(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <PageHeader
        overline="Administration"
        title="Admin Settings"
        description="Platform-wide defaults for how many company profiles an account may hold."
        testid="page-admin-settings"
      />

      <Content>
        <form onSubmit={saveDefaults} className="card-flat p-5 mb-6 max-w-xl"
              data-testid="admin-settings-form">
          <div className="flex items-center gap-2 mb-4">
            <Settings2 className="w-4 h-4 text-[var(--primary)]" />
            <span className="overline">Company limits</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600 block mb-1.5">
                Default for new accounts
              </label>
              <input type="number" min={1} max={defaults.max_company_limit}
                     className={inputCls} value={defaults.default_company_limit}
                     onChange={(e) =>
                       setDefaults((d) => ({ ...d, default_company_limit: Number(e.target.value) }))
                     }
                     data-testid="setting-default-limit" />
              <div className="text-[11px] text-gray-400 mt-1">
                Applied to accounts created from now on.
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-600 block mb-1.5">
                Ceiling an admin may grant
              </label>
              <input type="number" min={defaults.default_company_limit} max={100}
                     className={inputCls} value={defaults.max_company_limit}
                     onChange={(e) =>
                       setDefaults((d) => ({ ...d, max_company_limit: Number(e.target.value) }))
                     }
                     data-testid="setting-max-limit" />
              <div className="text-[11px] text-gray-400 mt-1">
                Upper bound for the per-account limits below.
              </div>
            </div>
          </div>
          <button type="submit" disabled={saving}
                  className="btn-primary text-sm h-10 px-4 mt-4 inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="admin-settings-save">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save defaults"}
          </button>
        </form>

        <div className="card-flat overflow-hidden">
          <div className="px-5 py-4 border-b border-[var(--border)] flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-400" />
            <span className="overline">Per-account limits</span>
          </div>

          {loading ? (
            <div className="p-8 text-center text-sm text-gray-500">Loading accounts…</div>
          ) : (
            <table className="w-full text-sm" data-testid="admin-limit-table">
              <thead>
                <tr className="border-b border-[var(--border)] text-left">
                  <th className="px-5 py-3 text-xs text-gray-500 font-medium">Account</th>
                  <th className="px-5 py-3 text-xs text-gray-500 font-medium">Role</th>
                  <th className="px-5 py-3 text-xs text-gray-500 font-medium">Companies</th>
                  <th className="px-5 py-3 text-xs text-gray-500 font-medium">Limit</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const used = u.company_count ?? 0;
                  const limit = u.company_limit ?? defaults.default_company_limit;
                  const over = used > limit;
                  return (
                    <tr key={u.id} className="border-b border-[var(--border)] last:border-0"
                        data-testid={`admin-limit-row-${u.id}`}>
                      <td className="px-5 py-3">
                        <div className="truncate">{u.name || "—"}</div>
                        <div className="text-xs text-gray-400 truncate">{u.email}</div>
                      </td>
                      <td className="px-5 py-3 text-xs capitalize">{u.role}</td>
                      <td className="px-5 py-3 num">
                        <span className={over ? "text-red-600" : ""}>{used}</span>
                        {over && (
                          <span className="text-[11px] text-red-600 ml-1.5">over limit</span>
                        )}
                      </td>
                      <td className="px-5 py-3 num">
                        {editingId === u.id ? (
                          <input
                            type="number"
                            min={1}
                            max={defaults.max_company_limit}
                            autoFocus
                            className="w-20 h-8 px-2 border border-[var(--border)] rounded text-sm"
                            value={draftLimit}
                            onChange={(e) => setDraftLimit(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && draftLimit) saveUserLimit(u);
                              if (e.key === "Escape") setEditingId(null);
                            }}
                            data-testid={`admin-limit-input-${u.id}`}
                          />
                        ) : (
                          <span data-testid={`admin-limit-value-${u.id}`}>{limit}</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right">
                        {editingId === u.id ? (
                          <div className="flex gap-2 justify-end">
                            <button onClick={() => saveUserLimit(u)} disabled={busyId === u.id}
                                    className="text-xs px-3 h-8 rounded-md bg-[var(--primary)] text-white disabled:opacity-50"
                                    data-testid={`admin-limit-save-${u.id}`}>
                              Save
                            </button>
                            <button onClick={() => setEditingId(null)}
                                    className="text-xs px-3 h-8 border border-[var(--border)] rounded-md hover:bg-gray-50">
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setEditingId(u.id);
                              setDraftLimit(String(limit));
                            }}
                            className="text-xs px-3 h-8 border border-[var(--border)] rounded-md hover:bg-gray-50"
                            data-testid={`admin-limit-edit-${u.id}`}
                          >
                            Change
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <p className="text-xs text-gray-500 mt-4 max-w-xl">
          Raising a limit only lets an account create more company profiles. It does not
          grant any administrator access to that account’s data.
        </p>
      </Content>
    </>
  );
}